// Client-side thumbnail generation for formats the browser cannot render in an
// <img>: .psd (Photoshop) and .ai/.pdf (PDF-compatible). We rasterize the file
// to a small PNG at upload time so the asset grid has a real preview. Best-effort
// throughout: returns null on any failure so the upload still succeeds and the
// card falls back to the plain file-extension label.

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const MAX_THUMB_SIZE = 512;

// Non-image formats we know how to rasterize. .eps is intentionally excluded -
// pdf.js does not parse it; it keeps the label fallback.
export function canRenderThumb(name: string): boolean {
  return /\.(psd|ai|pdf)$/i.test(name);
}

// Dispatches by extension. .ai is usually a PDF-compatible file (Illustrator's
// default), so it goes through the same pdf.js path as .pdf - non-compatible .ai
// files simply fail to parse and return null.
export async function renderFileThumb(file: File): Promise<Blob | null> {
  if (/\.psd$/i.test(file.name)) return renderPsdThumb(file);
  if (/\.(ai|pdf)$/i.test(file.name)) return renderPdfThumb(file);
  return null;
}

async function renderPsdThumb(file: File): Promise<Blob | null> {
  // Lazy import - ag-psd is only pulled into the chunk when a PSD is uploaded.
  const { readPsd } = await import('ag-psd');
  const buffer = await file.arrayBuffer();
  // skipLayerImageData keeps memory down - we only need the merged composite.
  const psd = readPsd(buffer, { skipLayerImageData: true, skipThumbnail: false });
  const source = psd.canvas;
  if (!source) return null;
  return downscaleToPng(source, MAX_THUMB_SIZE);
}

async function renderPdfThumb(file: File): Promise<Blob | null> {
  // Lazy import - pdf.js only loads when an .ai/.pdf is uploaded.
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(buffer) }).promise;
  try {
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(1, MAX_THUMB_SIZE / Math.max(base.width, base.height));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    return toPng(canvas);
  } finally {
    // Release the parsed document (and its worker buffers) promptly.
    await pdf.destroy();
  }
}

function downscaleToPng(source: HTMLCanvasElement, maxSize: number): Promise<Blob | null> {
  const scale = Math.min(1, maxSize / Math.max(source.width, source.height));
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(source, 0, 0, w, h);
  return toPng(out);
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}
